import { call, StrictEffect, take } from '@redux-saga/core/effects';
import { configureStore } from '@reduxjs/toolkit';
import { combineReducers, ReducersMapObject } from 'redux';
import createSagaMiddleware, { buffers, eventChannel } from 'redux-saga';
import globalApi from '../globalApi';
import { GlobalStore, ModuleInitFn, ModuleInitFnFrom, ModuleInitOptions } from '../types';

/**
 * Initialization task for a module
 */
type InitSagaTask = [(opts?: ModuleInitOptions) => Generator, ModuleInitOptions];

/**
 * Options for new modules
 */
interface ModuleOptions<
    R extends ReducersMapObject,
    D extends ReadonlyArray<ModuleInitFn>,
> {
    /**
     * Module initialization functions of dependency modules
     */
    deps?: D;
    /**
     * Reducers to be added to the global store
     */
    reducers?: R;
    /**
     * Module initialization generator function
     */
    onInitSaga?: (opts: ModuleInitOptions) => Generator<StrictEffect>;
}

// type ModuleState<F extends () => Store<{}>> = ReturnType<ReturnType<F>['getState']>;

/**
 * Global Redux store
 */
let _store: GlobalStore;

/**
 * Map of reducers used by the store's combined reducer
 *
 * Initialized with the reducer of the global API slice
 */
const _reducers: ReducersMapObject = {
    [globalApi.reducerPath]: globalApi.reducer,
};

/**
 * Sends tasks to the module init task channel
 */
let _emitInitSagaTask: (task: InitSagaTask) => void;

/**
 * Initializes the global store and creates a module initialization function.
 */
const createModuleInit = <
    R extends ReducersMapObject,
    D extends ReadonlyArray<ModuleInitFn>
>(
    moduleOpts: ModuleOptions<R, D>
): ModuleInitFnFrom<R, D> => {
    _initStore();

    let isInitialized = false;
    const fn = ((initOpts?: ModuleInitOptions) => {
        if (isInitialized) {
            return _store;
        }

        isInitialized = true;

        // Update the global reducer
        Object.assign(_reducers, moduleOpts.reducers);
        const dReducer = combineReducers(_reducers);
        _store.replaceReducer(dReducer);

        // Queue up dependencies' init tasks
        if (moduleOpts.deps) {
            for (const dep of moduleOpts.deps) {
                dep(initOpts);
            }
        }

        // Run own init task
        if (moduleOpts.onInitSaga) {
            _emitInitSagaTask([moduleOpts.onInitSaga, moduleOpts]);
        }

        return _store;
    });
    return fn as unknown as ModuleInitFnFrom<R, D>;
};

export default createModuleInit;

/**
 * Initialize the global Redux store.
 */
const _initStore = () => {
    if (_store) {
        return;
    }

    const sagaMiddleware = createSagaMiddleware();

    _store = configureStore({
        reducer: combineReducers(_reducers),
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware()
                .concat(globalApi.middleware)
                .prepend(sagaMiddleware)
    });

    const initSagaTasks = eventChannel<InitSagaTask>((emit) => {
        _emitInitSagaTask = emit;
        return () => {};
    }, buffers.expanding());

    sagaMiddleware.run(function* () {
        while (true) {
            const [onInitSaga, initOpts]: InitSagaTask = yield take(initSagaTasks);
            yield call(onInitSaga, initOpts);
        }
    });
};
